import type {
  EnhancedScopeData,
  LoadedGraphWithViewpointMode,
} from '@ardoq/data-model';
import type { Locale } from '@ardoq/locale';
import {
  type BlocksArrangement,
  clearedArrangement,
  mergeArrangements,
  parseArrangements,
} from 'tabview/assistantBlocks/arrangementRecipe';
import {
  compileArrangement,
  toChildArrangements,
  treeShownLinks,
  type ShownLink,
} from 'tabview/assistantBlocks/compileArrangement';
import {
  type GroupLink,
  toGroupLinks,
} from 'tabview/assistantBlocks/groupLinks';
import { findNode } from 'tabview/assistantBlocks/arrangeableNodes';
import {
  childrenOf,
  resolveContainerArrangements,
  withChildArrangements,
} from 'tabview/assistantBlocks/resolveArrangements';
import {
  type BlocksViewNode,
  type BlocksViewSettings,
  type LayoutData,
  LayoutType,
  type NodeLayoutData,
} from '../types';
import { isGroup } from '../viewModel$/util';
import { getNodeLayoutIndex, isLayoutSet } from './util';
import { fixupLayout, resetLayout, restoreLayout, stripLayout } from './layout';
import {
  alignLinkedChildren,
  inPlacementOrder,
  linkGroups,
} from './linkedGroups';

type Cell = Omit<NodeLayoutData, 'open'>;

/** The recipe compiled against the view's tree, ready to fill in what the stored layout lacks. */
export type ArrangedLayout = {
  /** Keyed like `layoutData`, by `getNodeLayoutIndex`. */
  cells: Record<string, Cell>;
  /** Every group the recipe lays out, directly or through a `childLayout`. */
  arrangedContainerIds: (string | null)[];
  /** Groups whose arrangement is cleared, which go back to the smart layout when replaced. */
  clearedContainerIds: (string | null)[];
  /** Groups the recipe still lays out: arranged and not cleared. A hand reset clears these. */
  coveredContainerIds: (string | null)[];
  /** Parent/child links a 'tree' region's layout shows, so drawing them again is redundant. */
  treeLinks: ShownLink[];
  /** Groups whose children the recipe lays out afresh, discarding their stored layout. */
  replacedContainerIds: (string | null)[];
  /** Sibling groups whose blocks line up with each other. */
  groupLinks: GroupLink[];
};

type ToArrangedLayoutArgs = {
  root: BlocksViewNode;
  scopeData: EnhancedScopeData;
  arrangements: BlocksArrangement[];
  pendingContainerIds: (string | null)[];
  locale: Locale;
};

/** A logical cell in a group's own grid, on the view's odd node tracks. */
const toTrackCell = ({ col, row, colSpan, rowSpan }: Cell): Cell => ({
  col: 2 * col + 1,
  row: 2 * row + 1,
  colSpan: 2 * colSpan - 1,
  rowSpan: 2 * rowSpan - 1,
});

const containerOf = (
  root: BlocksViewNode,
  containerId: string | null
): BlocksViewNode | null =>
  containerId === null ? root : findNode(root, containerId);

const derivedContainerIds = (
  scopeData: EnhancedScopeData,
  root: BlocksViewNode,
  arrangement: BlocksArrangement
): (string | null)[] => {
  const children = childrenOf(scopeData, root, arrangement.containerId);
  return children
    ? toChildArrangements(arrangement, children).map(
        ({ containerId }) => containerId
      )
    : [];
};

export const toArrangedLayout = ({
  root,
  scopeData,
  arrangements,
  pendingContainerIds,
  locale,
}: ToArrangedLayoutArgs): ArrangedLayout => {
  const resolved = withChildArrangements(scopeData, root, arrangements);
  const cleared = resolved.filter(
    ({ areas, regions }) => areas.length === 0 && regions.length === 0
  );
  const cells = resolved.flatMap(arrangement => {
    const container = containerOf(root, arrangement.containerId);
    const children = childrenOf(scopeData, root, arrangement.containerId);
    if (!container?.children || !children) return [];
    const childrenById = new Map(
      container.children.map(child => [child.id, child])
    );
    return compileArrangement({ arrangement, children, locale }).flatMap(
      ({ nodeId, col, row, colSpan, rowSpan }) => {
        const child = childrenById.get(nodeId);
        return child
          ? [
              [
                getNodeLayoutIndex(child),
                toTrackCell({ col, row, colSpan, rowSpan }),
              ] as const,
            ]
          : [];
      }
    );
  });
  const pending = new Set(pendingContainerIds);
  const groupLinks = toGroupLinks({
    scopeData,
    root,
    resolved: resolveContainerArrangements({
      scopeData,
      root,
      locale,
      arrangements: resolved,
    }),
  });
  return {
    cells: Object.fromEntries(cells),
    arrangedContainerIds: resolved.map(({ containerId }) => containerId),
    clearedContainerIds: cleared.map(({ containerId }) => containerId),
    coveredContainerIds: resolved
      .filter(arrangement => !cleared.includes(arrangement))
      .map(({ containerId }) => containerId),
    treeLinks: resolved.flatMap(arrangement => {
      const children = childrenOf(scopeData, root, arrangement.containerId);
      return children ? treeShownLinks(arrangement, children) : [];
    }),
    replacedContainerIds: [
      ...new Set(
        arrangements
          .filter(({ containerId }) => pending.has(containerId))
          .flatMap(arrangement => [
            arrangement.containerId,
            ...derivedContainerIds(scopeData, root, arrangement),
          ])
      ),
    ],
    groupLinks,
  };
};

type ArrangedLayoutForArgs = {
  root: BlocksViewNode | null;
  loadedGraph: Pick<LoadedGraphWithViewpointMode, 'scopeData'>;
  viewSettings: Pick<
    BlocksViewSettings,
    'arrangements' | 'pendingArrangementContainerIds'
  >;
  locale: Locale;
};

/** The recipe compiled for this view, or null when there is no tree or no recipe. */
export const arrangedLayoutFor = ({
  root,
  loadedGraph,
  viewSettings: { arrangements, pendingArrangementContainerIds },
  locale,
}: ArrangedLayoutForArgs): ArrangedLayout | null => {
  if (!root || !arrangements?.length) return null;
  return toArrangedLayout({
    root,
    scopeData: loadedGraph.scopeData,
    arrangements: parseArrangements(arrangements),
    pendingContainerIds: pendingArrangementContainerIds ?? [],
    locale,
  });
};

const overlaps = (a: Cell, b: Cell): boolean =>
  a.col < b.col + b.colSpan &&
  b.col < a.col + a.colSpan &&
  a.row < b.row + b.rowSpan &&
  b.row < a.row + a.rowSpan;

const setCell = (node: BlocksViewNode, cell: Cell | null): void => {
  node.col = cell?.col ?? NaN;
  node.row = cell?.row ?? NaN;
  node.colSpan = cell?.colSpan ?? NaN;
  node.rowSpan = cell?.rowSpan ?? NaN;
};

const replaceChildren = (
  group: BlocksViewNode,
  cells: ArrangedLayout['cells']
) =>
  group.children?.forEach(child =>
    setCell(child, cells[getNodeLayoutIndex(child)] ?? null)
  );

/** A child with no stored cell takes its arranged one, unless a laid-out sibling is already there. */
const fillChildren = (
  group: BlocksViewNode,
  cells: ArrangedLayout['cells']
) => {
  const children = group.children ?? [];
  const taken: Cell[] = children.filter(isLayoutSet);
  children
    .filter(child => !isLayoutSet(child))
    .forEach(child => {
      const cell = cells[getNodeLayoutIndex(child)];
      if (!cell || taken.some(other => overlaps(other, cell))) return;
      setCell(child, cell);
      taken.push(cell);
    });
};

/**
 * Writes the recipe into the tree. Replaced groups get the recipe's cells outright; elsewhere a
 * stored cell wins, so hand edits stay and only new or unplaced children follow the recipe.
 * Anything left without a cell is for `fixupLayout`.
 */
export const applyArrangedLayout = (
  root: BlocksViewNode,
  { cells, replacedContainerIds, clearedContainerIds }: ArrangedLayout
): void => {
  const replaced = new Set(replacedContainerIds);
  const cleared = new Set(clearedContainerIds);
  const walk = (group: BlocksViewNode, containerId: string | null): void => {
    if (!isGroup(group)) return;
    if (replaced.has(containerId) && cleared.has(containerId)) {
      resetLayout(group, LayoutType.Smart);
    } else if (replaced.has(containerId)) replaceChildren(group, cells);
    else fillChildren(group, cells);
    group.children.forEach(child => walk(child, child.id));
  };
  walk(root, null);
};

const hasLayout = (layoutData: LayoutData | null): layoutData is LayoutData =>
  Boolean(layoutData && Object.keys(layoutData).length);

/**
 * Lays the tree out from the stored layout and the recipe. Without a stored layout, the groups the
 * recipe covers follow it and the rest get the smart layout. Returns whether a pending recipe was
 * applied, which then needs saving so a reload doesn't apply it again over later hand edits.
 */
export const layOutWithArrangements = (
  root: BlocksViewNode,
  storedLayout: LayoutData | null,
  arrangedLayout: ArrangedLayout
): boolean => {
  const cleared = new Set(arrangedLayout.clearedContainerIds);
  const replacedContainerIds = hasLayout(storedLayout)
    ? arrangedLayout.replacedContainerIds
    : arrangedLayout.arrangedContainerIds.filter(
        containerId => !cleared.has(containerId)
      );
  if (hasLayout(storedLayout)) restoreLayout(root, storedLayout);
  else resetLayout(root, LayoutType.Smart, false);
  applyArrangedLayout(root, { ...arrangedLayout, replacedContainerIds });
  fixupLayout(root);
  linkGroups(root, arrangedLayout.groupLinks);
  const replaced = new Set(replacedContainerIds);
  inPlacementOrder(arrangedLayout.groupLinks)
    .filter(
      ({ containerId, sourceGroupId, targetGroupId }) =>
        replaced.has(containerId) ||
        replaced.has(sourceGroupId) ||
        replaced.has(targetGroupId)
    )
    .forEach(link => alignLinkedChildren(root, link));
  stripLayout(root);
  return arrangedLayout.replacedContainerIds.length > 0;
};

const toContainerId = (group: BlocksViewNode): string | null =>
  group.parent ? group.id : null;

/**
 * The recipe once these groups have had a layout type applied by hand: cleared for the groups it
 * covers, and the very same array when it covers none of them.
 */
export const withoutArrangementsFor = (
  arrangements: BlocksArrangement[],
  coveredContainerIds: (string | null)[],
  groups: Iterable<BlocksViewNode>
): BlocksArrangement[] => {
  const covered = new Set(coveredContainerIds);
  const cleared = [...groups]
    .map(toContainerId)
    .filter(containerId => covered.has(containerId))
    .map(clearedArrangement);
  return cleared.length
    ? mergeArrangements(arrangements, cleared)
    : arrangements;
};

const isEmptyList = (value: unknown): boolean =>
  Array.isArray(value) && value.length === 0;

/**
 * Whether a settings change only records what the view already shows: the layout it saved after
 * applying a pending recipe, and the cleared pending marker. Rebuilding for it would reset the
 * viewport and the undo stack for nothing.
 */
export const isAppliedRecipeAcknowledgement = (
  changedSettings: [PropertyKey, unknown][]
): boolean =>
  changedSettings.length > 0 &&
  changedSettings.every(
    ([key, value]) =>
      key === 'layoutData' ||
      key === 'arrangements' ||
      (key === 'pendingArrangementContainerIds' && isEmptyList(value))
  );
